﻿import { CrosswindCalculator, RunwayPair } from '@fe/crosswind';
import { Window } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { AirportWindDataDto, MessageType } from '../../services/actor-messages';
import { AirportService, RunwayPair as ApiRunwayPair } from '../../services/axios/axios-api-v2-generated';
import { useEvent } from '../contexts/event-bus';
import { useAppSelector } from '../hooks/store-hooks';
import { useFlightWorker } from '../hooks/use-flight-worker';

// Converts a runway designator like "28L" or "09" to a heading in degrees.
function runwayDesignatorToHeading(designator: string): number {
    const match = designator.match(/^(\d{2})/);
    if (!match) {
        return 0;
    }
    return parseInt(match[1], 10) * 10;
}

interface RunwayWindsState {
    data: AirportWindDataDto;
    label: string;
}

function mapRunwayUsage(usage: number | null | undefined): 'arrival' | 'departure' | 'both' | undefined {
    switch (usage) {
        case 0:
            return 'arrival';
        case 1:
            return 'departure';
        case 2:
            return 'both';
        default:
            return undefined;
    }
}

function toRunwayPair(api: ApiRunwayPair): RunwayPair {
    return {
        base: {
            id: api.base?.id ?? '',
            heading: api.base?.heading ?? 0,
            usage: mapRunwayUsage(api.base?.usage)
        },
        recip: {
            id: api.recip?.id ?? '',
            heading: api.recip?.heading ?? 0,
            usage: mapRunwayUsage(api.recip?.usage)
        }
    } satisfies RunwayPair;
}

const KEY = ['airport-data'];
function RunwayWindsWindow() {
    const [airportId, setAirportId] = useState<string | null>(null);
    const { data: airportData, isLoading } = useQuery({
        queryKey: [KEY, airportId],
        queryFn: () => AirportService.getAirportData({ airportId: airportId ?? '' }, { withAuthorization: true }),
        enabled: airportId !== null
    });
    const [state, setState] = React.useState<RunwayWindsState | null>(null);
    const isDark = useAppSelector((x) => x.settings.isDark);
    const worker = useFlightWorker();

    useEvent(
        'show-runway-winds',
        React.useCallback(
            async ({ airportId, label, flightRunway }: { airportId: string; label: string; flightRunway?: string | null }) => {
                const data = await worker.actor.sendAsync({
                    type: MessageType.getAirportWindData,
                    data: { airportId, flightRunway }
                });
                setAirportId(airportId);
                setState({ data, label });
            },
            [worker]
        )
    );

    if (!state) {
        return null;
    }

    const { data, label } = state;

    const windDirection = data.metarWindDirection === 'variable' || data.metarWindDirection == null ? 0 : data.metarWindDirection;
    const windSpeed = data.metarWindSpeed ?? 0;
    const gustSpeed = data.metarWindGusts ?? undefined;

    const defaultHeading = data.activeRunways.length > 0 ? runwayDesignatorToHeading(data.activeRunways[0]) : 0;
    const defaultRunway = data.activeRunways.length > 0 ? data.activeRunways[0] : undefined;

    const windSources =
        data.metarWindSpeed != null
            ? {
                  metar: {
                      direction: windDirection,
                      speed: windSpeed,
                      gust: gustSpeed
                  }
              }
            : undefined;

    const title = `Crosswind calculator — ${data.airportName ? `${data.airportName}` : ''}`;

    const runwayMissing = data.activeRunways.length === 0;
    const windMissing = data.metarWindSpeed == null;
    const bannerCount = (runwayMissing ? 1 : 0) + (windMissing ? 1 : 0);

    const windowHeight = 560 + bannerCount * 52;

    return (
        <Window title={title} initialWidth={940} height={windowHeight} modal={false} resizable={false} onClose={() => setState(null)}>
            <div style={{ maxWidth: 920, width: '100%' }} className="d-flex flex-column h-100">
                {(runwayMissing || windMissing) && (
                    <div className="d-flex flex-column gap-1 px-2 pt-2">
                        {runwayMissing && (
                            <div className="k-messagebox k-messagebox-warning small m-0" style={{ fontSize: '.85em' }}>
                                <span className="k-icon k-i-warning"></span>
                                No active runway data for this flight. Use the runway selector to set the correct runway manually.
                            </div>
                        )}
                        {windMissing && (
                            <div className="k-messagebox k-messagebox-warning small m-0" style={{ fontSize: '.85em' }}>
                                <span className="k-icon k-i-warning"></span>
                                No METAR wind data available for <strong>{label}</strong>. Use the <strong>User</strong> wind source to
                                enter wind manually.
                            </div>
                        )}
                    </div>
                )}
                {isLoading && <Loader />}
                {!isLoading && (
                    <div className="flex-grow-1 d-flex align-items-center">
                        <CrosswindCalculator
                            iata={data.iata ?? undefined}
                            icao={data.icao ?? undefined}
                            magneticVariation={airportData?.magneticVariation}
                            runways={airportData?.runwayPairs?.map((p) => toRunwayPair(p))}
                            defaultRunway={defaultRunway}
                            defaultHeading={defaultHeading}
                            windSources={windSources}
                            defaultWindSource={windSources ? 'metar' : 'user'}
                            theme={isDark ? 'dark' : 'light'}
                            showRunwayTable={true}
                            showHeadingOnCompass={true}
                        />
                    </div>
                )}
            </div>
        </Window>
    );
}

export default RunwayWindsWindow;
